import React from 'react';
import styled from 'styled-components';
import { Exam } from './examData';

const Container = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 40px 24px;
    min-height: calc(100vh - 56px);
    background-color: #f8fafc;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    color: #00205B;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    padding: 8px 0;
    margin-bottom: 20px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Roboto', sans-serif;

    &:hover {
        text-decoration: underline;
    }
`;

const HeaderArea = styled.div`
    margin-bottom: 40px;
`;

const Title = styled.h1`
    color: #00205B;
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 6px 0;
`;

const Subtitle = styled.p`
    color: #64748b;
    font-size: 1rem;
    margin: 0;
`;

const Section = styled.div`
    margin-bottom: 40px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 2px solid #cbd5e1;
    margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
    color: #1e293b;
    font-size: 1.15rem;
    font-weight: 800;
    margin: 0;
`;

const ExamCount = styled.span`
    color: #94a3b8;
    font-size: 0.85rem;
    font-weight: 600;
`;

const ExamRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    flex-wrap: wrap;
`;

const ExamCard = styled.div`
    background: #00205B;
    border-radius: 12px;
    padding: 0;
    min-width: 280px;
    flex: 1;
    max-width: 400px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    overflow: hidden;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 32, 91, 0.25);
    }
`;

const CardContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 20px 24px;
`;

const CardLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const CardSemester = styled.span`
    color: #ffffff;
    font-size: 1.05rem;
    font-weight: 700;
`;

const CardStats = styled.span`
    color: #ffffffaa;
    font-size: 0.85rem;
    font-weight: 500;
`;

const CardRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const GoLabel = styled.span`
    color: #60a5fa;
    font-size: 0.9rem;
    font-weight: 700;
`;

const ChevronIcon = styled.span`
    color: #ffffffaa;
    font-size: 1rem;
    transition: transform 0.2s ease;

    ${ExamCard}:hover & {
        transform: translateX(3px);
        color: #ffffff;
    }
`;

const EmptyCard = styled.div`
    background: #e2e8f0;
    border-radius: 12px;
    padding: 20px 24px;
    min-width: 280px;
    flex: 1;
    max-width: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 0.9rem;
    font-weight: 600;
    font-style: italic;
`;

const NoExams = styled.p`
    color: #94a3b8;
    font-size: 1rem;
    text-align: center;
    margin-top: 60px;
`;

interface ExamListProps {
    courseId: string;
    exams: Exam[];
    onBack: () => void;
    onSelectExam: (exam: Exam) => void;
    timedMode?: boolean;
}

const ExamList: React.FC<ExamListProps> = ({ courseId, exams, onBack, onSelectExam, timedMode }) => {
    const courseExams = exams.filter(e => e.courseId === courseId);

    const midterm1 = courseExams.filter(e => e.title.toLowerCase().includes('midterm 1'));
    const midterm2 = courseExams.filter(e => e.title.toLowerCase().includes('midterm 2'));
    const finals = courseExams.filter(e => e.title.toLowerCase().includes('final'));

    const getLabel = (exam: Exam) => {
        return exam.title
            .replace(/Midterm \d\s*/i, '')
            .replace(/Final\s*/i, '')
            .trim();
    };

    const renderCards = (list: Exam[]) => {
        if (list.length === 0) return <EmptyCard>Coming soon</EmptyCard>;
        return list.map(exam => (
            <ExamCard key={exam.id} onClick={() => onSelectExam(exam)}>
                <CardContent>
                    <CardLeft>
                        <CardSemester>{getLabel(exam)}</CardSemester>
                        <CardStats>{exam.questions.length} questions</CardStats>
                    </CardLeft>
                    <CardRight>
                        <GoLabel>Go</GoLabel>
                        <ChevronIcon>&#8250;</ChevronIcon>
                    </CardRight>
                </CardContent>
            </ExamCard>
        ));
    };

    const sections = [
        { title: 'Midterm 1', list: midterm1 },
        { title: 'Midterm 2', list: midterm2 },
        { title: 'Final', list: finals },
    ];

    return (
        <Container>
            <BackButton onClick={onBack}>&larr; Back to {courseId}</BackButton>
            <HeaderArea>
                <Title>{courseId} — {timedMode ? 'Timed Exams' : 'Past Exams'}</Title>
                <Subtitle>{timedMode ? 'Select an exam — you will have 1 hour to complete it' : 'Select an exam to start practicing'}</Subtitle>
            </HeaderArea>

            {courseExams.length === 0 ? (
                <NoExams>No exams available yet for this course.</NoExams>
            ) : (
                sections.map(section => (
                    <Section key={section.title}>
                        <SectionHeader>
                            <SectionTitle>{section.title}</SectionTitle>
                            <ExamCount>{section.list.length} exam{section.list.length !== 1 ? 's' : ''}</ExamCount>
                        </SectionHeader>
                        <ExamRow>{renderCards(section.list)}</ExamRow>
                    </Section>
                ))
            )}
        </Container>
    );
};

export default ExamList;
